import Vue from 'vue';
import { VNode } from 'vue';
import { DirectiveBinding } from 'vue/types/options';
import FeatureGating from '@/services/FeatureGating';

const visibilityThreshold = 0.01;

let root: Element | null = null;

interface LazyLoadIntersectionObserver extends IntersectionObserver {
  POLL_INTERVAL?: number;
  USE_MUTATION_OBSERVER?: boolean;
}

interface LazyLoadDirectiveContext extends Vue {
  visible: boolean;
  observer: LazyLoadIntersectionObserver;
}

export const LazyLoad = {
  inserted(el: HTMLElement, binding: DirectiveBinding, vnode: VNode): void {
    if (!vnode.context) {
      return;
    }
    // Pass in flag as part of directive binding to toggle lazy load
    if (!binding.value) {
      (<LazyLoadDirectiveContext>vnode.context).visible = true;
      return;
    }

    // NOTE (Limitation): rootMargin only triggers horizontal adjacent elements and not
    // vertical adjacent elements. The reason for this is our implementation of the
    // vertical scroll overflow and how it works with intersections off screen.
    // - Related: https://github.com/w3c/IntersectionObserver/issues/260

    const avatarBuilderCategoryContainerSelector = FeatureGating.isUpdatedTraitNavEnabled()
      ? '.avatar-builder-category-container-updated'
      : '.avatar-builder-category-container';
    root = document.querySelector(avatarBuilderCategoryContainerSelector);

    const observerOptions = {
      root,
      rootMargin: '100%',
      threshold: visibilityThreshold,
    };

    // NOTE (Future): this can be expanded to vertically pre-render options
    // outside of the visible area using the trait option index.

    const observer: LazyLoadIntersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // Round intersection ratio to two decimal places so that this expression is properly
        // evaluated on high precision browsers
        if (
          entry.isIntersecting &&
          vnode.context &&
          !(<LazyLoadDirectiveContext>vnode.context).visible
        ) {
          (<LazyLoadDirectiveContext>vnode.context).visible =
            +entry.intersectionRatio.toFixed(2) >= visibilityThreshold;
        }
      });
    }, observerOptions);

    // Set observer properties before initiating observation
    observer.POLL_INTERVAL = 100;
    observer.USE_MUTATION_OBSERVER = false;

    observer.observe(el);
    (<LazyLoadDirectiveContext>vnode.context).observer = observer;
  },
  unbind(el: HTMLElement, binding: DirectiveBinding, vnode: VNode): void {
    if (!binding.value) {
      return;
    }
    // Unobserve when the component is removed from the DOM
    // so the observer stack doesn't get out of control
    if ((<LazyLoadDirectiveContext>vnode.context)?.observer) {
      (<LazyLoadDirectiveContext>vnode.context).observer.unobserve(el);
    }
  },
};

Vue.directive('lazy-load', LazyLoad);
